import React from 'react';
import { useSearchParams } from 'react-router-dom';

// #region BookingShell
/**
 * The frame every public booking screen sits in: no dashboard chrome, just a
 * quiet wordmark and one centred card. Surface tokens flip with the viewer's
 * colour scheme, so there is nothing theme-specific here.
 */
export interface ShellOptions {
    /** The note beside the wordmark. */
    label?: React.ReactNode;
    /** The line under the card; null for none. */
    footer?: React.ReactNode;
    /** A full-bleed photo behind the card. */
    backdrop?: string;
    /** A form-width card instead of the booking width. */
    narrow?: boolean;
    /** A small card, for sign in. */
    compact?: boolean;
}

export const BookingShell: React.FunctionComponent<React.PropsWithChildren<ShellOptions>> = ({ children, ...options }): React.ReactElement => {
    // Embedded on another site (?embed=1 inside an iframe): just the card — the host page supplies the frame.
    const [params] = useSearchParams();
    if (params.get('embed') === '1' && window.parent !== window) {
        return <div data-embed-root className="p-px text-zinc-900 dark:text-zinc-100"><div className="rounded-2xl border border-surface-border bg-surface-raised">{children}</div></div>;
    }
    return <FullShell {...options}>{children}</FullShell>;
};

const FullShell: React.FunctionComponent<React.PropsWithChildren<ShellOptions>> = ({ children, label = 'Scheduling', backdrop, narrow, compact,
    footer = 'Times are shown in the zone you pick. A calendar invitation follows every booking.' }): React.ReactElement => (
    <div className={`relative min-h-screen text-zinc-900 dark:text-zinc-100 ${backdrop ? 'bg-zinc-900' : 'bg-surface-sunken'}`}>
        {backdrop ? (
            <>
                <img src={backdrop} alt="" aria-hidden className="fixed inset-0 h-full w-full object-cover" />
                <div aria-hidden className="fixed inset-0 bg-black/45" />
            </>
        ) : null}
        <div className={`relative mx-auto w-full ${compact ? 'max-w-md' : narrow ? 'max-w-2xl' : 'max-w-5xl'} px-4 py-6 sm:py-10`}>
            <div className="mb-5 flex items-center justify-between gap-3">
                <span className={`text-sm font-semibold tracking-[0.18em] uppercase ${backdrop ? 'text-white' : 'text-zinc-700 dark:text-zinc-300'}`}>Wallmates</span>
                <span className={`text-xs ${backdrop ? 'text-white/85' : 'text-zinc-500 dark:text-zinc-400'}`}>{label}</span>
            </div>
            <div className={`rounded-2xl border border-surface-border bg-surface-raised ${backdrop ? 'shadow-2xl' : 'shadow-sm'}`}>
                {children}
            </div>
            {footer ? <p className={`mt-6 text-center text-xs ${backdrop ? 'text-white/85' : 'text-zinc-500 dark:text-zinc-400'}`}>{footer}</p> : null}
        </div>
    </div>
);

/** A step label row: "1 Pick a meeting · 2 Pick a time · 3 Your details". */
export const Steps: React.FunctionComponent<{ current: number; labels: string[] }> = ({ current, labels }): React.ReactElement => (
    <ol className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {labels.map((label, i) => {
            const n = i + 1;
            const state = n < current ? 'done' : n === current ? 'now' : 'todo';
            return (
                <li key={label} className="flex items-center gap-x-1.5">
                    <span className={[
                        'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums',
                        state === 'now' ? 'bg-emerald-600 text-white' : state === 'done' ? 'bg-emerald-600/15 text-emerald-700 dark:text-emerald-400' : 'bg-surface-muted text-zinc-500'
                    ].join(' ')}>{n}</span>
                    <span className={state === 'now' ? 'font-medium text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 dark:text-zinc-400'}>{label}</span>
                    {i < labels.length - 1 ? <span className="ml-1.5 text-zinc-300 dark:text-zinc-600">·</span> : null}
                </li>
            );
        })}
    </ol>
);

/** A quiet error line used wherever a request fails. No toasts out here. */
export const InlineError: React.FunctionComponent<{ message: string | null }> = ({ message }): React.ReactElement | null =>
    message ? (
        <div role="alert" className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {message}
        </div>
    ) : null;
